import _ from 'lodash';
import Backbone from 'backbone';
import {bindAllMethods} from '@sinclair-digital/stl-common/src/backboneHelpers';

const PresentationModel = Backbone.Model.extend({
    urlRoot: '/api/presentation',
    idAttribute: 'uuid',
    defaults: {
        uuid: null,
        name: '',
        version: 1,
        pathUrl: '',
        status: 'draft',
        templateId: null,
        presentationReference: {
            key: 'blank-v1',
            name: 'Blank (common)',
            relativeImagePath: null,
            siteSlug: null,
            sourcedfrom: null,
        },
        presentationSection: {
            uuid: null,
        },
        presentationCategory: {
            name: 'content-story',
        },
        customHtml: {
            injectedMarkup: '',
        },
    },
    initialize() {
        bindAllMethods(this);
    },

    parse(data) {
        const model = data && (Array.isArray(data.data) && data.data.length > 0) ? data.data[0] : data;

        return model;
    },

    getModelId() {
        return this.attributes.uuid;
    },
    setModelId(uuid) {
        this.set({
            uuid,
        });
    },

    getAdzone() {
        return this.attributes.adZone;
    },
    setAdzone(zone) {
        this.set({
            adZone: zone,
        });
    },

    getName() {
        return this.attributes.name;
    },
    setName(name) {
        this.set({
            name,
        });
    },

    getRenderingData() {
        return this.attributes.renderingData;
    },
    setRenderingData(renderingData) {
        this.set({
            renderingData,
        });
    },

    getPublishAuthorizationDate() {
        return this.attributes.publishAuthorizationDate;
    },
    setPublishAuthorizationDate(date) {
        this.set({publishAuthorizationDate: date});
    },

    getUnpublishAuthorizationDate() {
        return this.attributes.unpublishAuthorizationDate;
    },
    setUnpublishAuthorizationDate(date) {
        this.set({unpublishAuthorizationDate: date});
    },

    getCreated() {
        return this.attributes.created;
    },

    getStatus() {
        return this.attributes.status;
    },
    setStatus(status) {
        const s = typeof status === 'string' ? status.toUpperCase() : '';

        const validStatuses = [
            'DOWN',
            'DRAFT',
            'PENDING',
            'PUBLISHED',
            'EXPIRED',
            'ARCHIVED',
            'SCHEDULED',
            'SUPERCEDED',
            'UNPUBLISHED',
            'UNSCHEDULED',
        ];

        const newStatus = validStatuses.includes(s) ? s : null;

        if (newStatus) {
            this.set({
                status: newStatus,
            });
        }
    },

    getVersion() {
        return this.attributes.version;
    },
    setVersion(version) {
        this.set({
            version,
        });
    },

    getPathUrl() {
        return this.attributes.pathUrl;
    },
    setPathUrl(path) {
        this.set({
            pathUrl: path,
        });
    },

    getPropertyTags() {
        return this.attributes.propertyTags;
    },
    setPropertyTags(tag) {
        const tags = this.getPropertyTags();

        if (!tags.includes(tag)) {
            tags.push(tag);
        }

        this.set({
            propertyTags: tags,
        });
    },

    getPresentationTypes() {
        return this.attributes.presentationTypes;
    },
    setPresentationTypes(type) {
        const types = this.getPresentationTypes();
        types.push(type);
        this.set({
            presentationTypes: types,
        });
    },

    getPropertyAttributes() {
        return this.attributes.propertyAttributes;
    },
    setPropertyAttributes(attr) {
        const attrs = this.getPropertyAttributes();
        attrs.push(attr);
        this.set({
            propertyAttributes: attr,
        });
    },

    getPresentationSection() {
        return this.attributes.presentationSection;
    },
    setPresentationSection(section) {
        this.set({
            presentationSection: {
                uuid: section,
            },
        });
    },

    getPresentationCategory() {
        return this.get('presentationCategory');
    },
    setPresentationCategory(category) {
        // TODO: siteTreeView._savePresentationHandler has logic for this as well...
        // recentralize where that logic comes from
        this.set({
            presentationCategory: {
                name: category,
            },
        });
    },

    getTemplateId() {
        return this.attributes.templateId;
    },
    setTemplateId(templateId) {
        this.set({
            templateId,
        });
    },

    setModelURL() {
        const base = '/api/presentation';

        if (base === this.url) {
            this.url = this.url + '/' + this.getModelId();
        }
    },

    setCustomHtml(customHtml){
        this.set({customHtml});
    },

    addComponentInstance(componentInstance) {
        const componentInstances = this.getComponentInstances();

        // TODO :: Get rid of component replication, possibly use contructor via model
        componentInstances[componentInstance.componentInstanceId] = {
            componentInstanceId: componentInstance.componentInstanceId,
            componentType: componentInstance.componentType,
            namespace: componentInstance.namespace,
            settings: componentInstance.settings,
        };

        this.setComponentInstances(componentInstances);
    },
    addComponentInstanceToRenderingData(componentInstance, targetComponentInstanceId, insertAboveTarget) {
        if (!targetComponentInstanceId) {
            return false;
        }
        this.recursiveAddComponentInstanceToRenderingData(
            componentInstance,
            targetComponentInstanceId,
            insertAboveTarget,
            this.getRenderingData().body,
        );
        this.addComponentInstance(componentInstance);

        return true;
    },
    recursiveAddComponentInstanceToRenderingData(
        componentInstance,
        targetComponentInstanceId,
        insertAboveTarget,
        renderingDataItem,
    ) {
        const that = this;

        if (Array.isArray(renderingDataItem)) {
            // The top level of renderingData is an array. Handle that special case.
            // .some() will short-circuit at the first "true" return, so we don't have to process the entire tree.
            return renderingDataItem.some(function(childItem) {
                return that.recursiveAddComponentInstanceToRenderingData(
                    componentInstance,
                    targetComponentInstanceId,
                    insertAboveTarget,
                    childItem,
                );
            });
        }

        if (!renderingDataItem.children || renderingDataItem.children.length < 1) {
            return false;
        }

        const foundItem = _.find(renderingDataItem.children, function(item) {
            return item.componentInstanceId === targetComponentInstanceId;
        });
        if (typeof foundItem !== 'undefined') {
            const newChildren = renderingDataItem.children;
            let index = newChildren.indexOf(foundItem);

            if (!insertAboveTarget) {
                index += 1;
            }

            newChildren.splice(index, 0, _.clone(componentInstance));
            delete newChildren[index].settings;
            delete newChildren[index].componentType;
            newChildren[index].locked = false;
            newChildren[index].containerName = '';
            renderingDataItem.children = newChildren;
            return true;
        }

        return renderingDataItem.children.some(function(childItem) {
            return that.recursiveAddComponentInstanceToRenderingData(
                componentInstance,
                targetComponentInstanceId,
                insertAboveTarget,
                childItem,
            );
        });
    },
    removeComponentInstance(componentInstanceId) {
        const componentInstances = this.getComponentInstances();

        delete componentInstances[componentInstanceId];
        this.setComponentInstances(componentInstances);
        this.removeComponentInstanceFromRenderingData(componentInstanceId);
        this.trigger('change');
    },
    removeComponentInstanceFromRenderingData(componentInstanceId) {
        this.recursiveRemoveComponentInstanceFromRenderingData(componentInstanceId, this.getRenderingData().body);
    },
    recursiveRemoveComponentInstanceFromRenderingData(componentInstanceId, renderingDataItem) {
        const that = this;

        if (Array.isArray(renderingDataItem)) {
            // The top level of renderingData is an array. Handle that special case.
            // .some() will short-circuit at the first "true" return, so we don't have to process the entire tree.
            return renderingDataItem.some(function(childItem) {
                return that.recursiveRemoveComponentInstanceFromRenderingData(componentInstanceId, childItem);
            });
        }

        if (!renderingDataItem.children || renderingDataItem.children.length < 1) {
            return false;
        }

        const foundItem = _.find(renderingDataItem.children, function(item) {
            return item.componentInstanceId === componentInstanceId;
        });
        if (typeof foundItem !== 'undefined') {
            const newChildren = _.reject(renderingDataItem.children, function(item) {
                return item.componentInstanceId === componentInstanceId;
            });
            renderingDataItem.children = newChildren;
            return true;
        }

        return renderingDataItem.children.some(function(childItem) {
            return that.recursiveRemoveComponentInstanceFromRenderingData(componentInstanceId, childItem);
        });
    },
    saveComponentInstance(componentInstance) {
        this.addComponentInstance(componentInstance);
    },
    _setState(state, options = {}) {
        const opts = Object.assign(
            {
                url: `/platform/property/status/${this.get('uuid')}/${state}`,
                method: 'POST',
            },
            options,
        );

        return this.save({}, opts);
    },
    publish(options) {
        return this._setState('PUBLISHED', options);
    },
    unpublish(options) {
        return this._setState('UNPUBLISHED', options);
    },
});
export default PresentationModel;
